import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getPublishedPageBySlug } from '../services/firestoreService';
import { getStaticPageURL, getStaticHTMLContent } from '../services/staticHostingService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import LoadingSpinner from '../components/LoadingSpinner';
import { generateCSSVariables, getColorPalette } from '../config/colorPalettes';
import { loadPaletteCSS } from '../utils/cssLoader.js';
import '../styles/pages/PublicPage.scss';

// ヘッドコンテンツをパースする関数
const parseHeadContent = (headContent) => {
    if (!headContent) return [];

    // <head>タグの中身を抽出（headタグがない場合はそのまま使用）
    let content = headContent;
    if (headContent.includes('<head>') && headContent.includes('</head>')) {
        content = headContent.split('</head>')[0].split('<head>')[1] || headContent;
    }

    // 各タグを抽出
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<html><head>${content}</head></html>`, 'text/html');
    const elements = Array.from(doc.head.children);

    return elements.map((element, index) => {
        const tagName = element.tagName.toLowerCase();
        const attributes = Array.from(element.attributes).reduce((acc, attr) => {
            acc[attr.name] = attr.value;
            return acc;
        }, {});

        // scriptタグの場合
        if (tagName === 'script') {
            return {
                type: 'script',
                props: {
                    ...attributes,
                    key: index,
                    dangerouslySetInnerHTML: { __html: element.innerHTML }
                }
            };
        }

        // metaタグの場合
        if (tagName === 'meta') {
            return {
                type: 'meta',
                props: { ...attributes, key: index }
            };
        }

        // linkタグの場合
        if (tagName === 'link') {
            return {
                type: 'link',
                props: { ...attributes, key: index }
            };
        }

        // styleタグの場合
        if (tagName === 'style') {
            return {
                type: 'style',
                props: {
                    ...attributes,
                    key: index,
                    dangerouslySetInnerHTML: { __html: element.innerHTML }
                }
            };
        }

        return null;
    }).filter(Boolean);
};

const PublicPage = () => {
    const { slug } = useParams();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [headElements, setHeadElements] = useState([]);
    const [isStaticHTML, setIsStaticHTML] = useState(false);
    const [staticHTMLContent, setStaticHTMLContent] = useState('');
    const [globalSettings, setGlobalSettings] = useState(null);

    // グローバル設定を取得する関数
    const fetchGlobalSettings = async (accountId) => {
        try {
            const settingsDoc = await getDoc(doc(db, 'settings', accountId));
            if (settingsDoc.exists()) {
                const settings = settingsDoc.data();
                console.log('✅ グローバル設定取得完了:', {
                    hasCustomHead: !!settings.customHead,
                    hasCustomBody: !!settings.customBody,
                    colorPalette: settings.colorPalette || 'default',
                    defaultPalette: settings.defaultPalette,
                    customPalettes: Object.keys(settings.customPalettes || {}).length
                });
                setGlobalSettings(settings);

                // デフォルトパレットまたはグローバル設定のパレットを適用
                const paletteToApply = settings.defaultPalette || settings.colorPalette || 'default';
                await applyColorPalette(paletteToApply, settings.customPalettes || {});

                return settings;
            } else {
                console.log('ℹ️ グローバル設定が見つかりません');
                // デフォルトカラーパレットを適用
                await applyColorPalette('default');
                return null;
            }
        } catch (error) {
            // 権限エラーの場合は詳細ログを出力してnullを返す
            if (error.code === 'permission-denied') {
                console.warn('🚫 グローバル設定へのアクセス権限がありません（未認証ユーザー）');
            } else {
                console.warn('⚠️ グローバル設定の取得に失敗:', error);
            }
            // デフォルトカラーパレットを適用
            await applyColorPalette('default');
            return null;
        }
    };


    // カラーパレットを適用する関数
    const applyColorPalette = async (paletteKey, customPalettes = {}) => {
        try {
            await loadPaletteCSS(paletteKey, customPalettes);
        } catch (error) {
            console.warn('⚠️ カラーパレットの適用に失敗:', error);
        }
    };

    useEffect(() => {
        const fetchPage = async () => {
            try {
                console.log('🔍 PublicPage: ページ取得開始:', { slug });

                // ルートパス（slug が undefined）の場合の特別処理
                const searchSlug = slug || '';
                console.log('📄 検索slug:', searchSlug);

                // ルートパスの場合、indexページを検索
                if (searchSlug === '') {
                    console.log('🔍 ルートパス: indexページを検索');
                    const indexPage = await getPageBySlug('index');
                    if (indexPage && indexPage.status === 'published') {
                        console.log('🏠 indexページを発見:', indexPage.title);
                        setPage(indexPage);

                        // グローバル設定を取得
                        let settings = null;
                        if (indexPage.accountId) {
                            settings = await fetchGlobalSettings(indexPage.accountId);
                        }

                        // ページ固有のカラーパレット設定を確認・適用
                        let paletteToApply = 'default';
                        if (indexPage.colorPalette) {
                            paletteToApply = indexPage.colorPalette;
                            console.log('🎨 indexページ固有のカラーパレットを使用:', paletteToApply);
                        } else if (settings && settings.defaultPalette) {
                            paletteToApply = settings.defaultPalette;
                            console.log('🎨 デフォルトカラーパレットを使用:', paletteToApply);
                        }

                        // カラーパレットを適用
                        await applyColorPalette(paletteToApply, settings?.customPalettes || {});

                        // headコンテンツとCSSを組み合わせてパース
                        let combinedHead = indexPage.head || '';

                        // グローバル設定のcustomHeadを追加
                        if (settings && settings.customHead) {
                            console.log('🔧 indexページにグローバル設定のcustomHeadを追加');
                            combinedHead += `\n<!-- Global Settings Custom Head -->\n${settings.customHead}`;
                        }

                        if (indexPage.css) {
                            combinedHead += `<style>${indexPage.css}</style>`;
                        }
                        if (combinedHead) {
                            const parsedElements = parseHeadContent(combinedHead);
                            setHeadElements(parsedElements);
                        }
                        setLoading(false);
                        return;
                    } else {
                        console.log('❌ indexページが見つからないか未公開');
                    }
                }

                // まず静的HTMLファイルをチェック
                const staticURL = await getStaticPageURL(searchSlug);

                if (staticURL) {
                    // 静的HTMLファイルが存在する場合
                    const htmlContent = await getStaticHTMLContent(staticURL);
                    if (htmlContent) {
                        console.log('✅ 静的HTMLファイルから読み込み成功');
                        setStaticHTMLContent(htmlContent);
                        setIsStaticHTML(true);
                        setLoading(false);
                        return;
                    }
                }

                // 静的HTMLファイルが存在しない場合、従来の方法でFirestoreから取得
                let fetchedPage = await getPublishedPageBySlug(searchSlug);

                // ルートパスでページが見つからない場合、'index' slugでも試行
                if (!fetchedPage && searchSlug === '') {
                    console.log('🏠 ルートページが見つからないため、indexページを検索');
                    fetchedPage = await getPublishedPageBySlug('index');
                }

                if (fetchedPage) {
                    console.log('✅ Firestoreからページ取得成功:', fetchedPage.title);
                    setPage(fetchedPage);

                    // グローバル設定を取得
                    let settings = null;
                    if (fetchedPage.accountId) {
                        settings = await fetchGlobalSettings(fetchedPage.accountId);
                    }

                    // ページ固有のカラーパレット設定を確認・適用
                    let paletteToApply = 'default';
                    if (fetchedPage.colorPalette) {
                        // ページ固有のパレットが設定されている場合
                        paletteToApply = fetchedPage.colorPalette;
                        console.log('🎨 ページ固有のカラーパレットを使用:', paletteToApply);
                    } else if (settings && settings.defaultPalette) {
                        // デフォルトパレットが設定されている場合
                        paletteToApply = settings.defaultPalette;
                        console.log('🎨 デフォルトカラーパレットを使用:', paletteToApply);
                    }

                    // カラーパレットを適用
                    await applyColorPalette(paletteToApply, settings?.customPalettes || {});

                    // headコンテンツとCSSを組み合わせてパース
                    let combinedHead = fetchedPage.head || '';

                    // グローバル設定のcustomHeadを追加
                    if (settings && settings.customHead) {
                        console.log('🔧 グローバル設定のcustomHeadを追加');
                        combinedHead += `\n<!-- Global Settings Custom Head -->\n${settings.customHead}`;
                    }

                    // CSSがある場合はheadに追加
                    if (fetchedPage.css) {
                        combinedHead += `<style>${fetchedPage.css}</style>`;
                    }

                    if (combinedHead) {
                        const parsedElements = parseHeadContent(combinedHead);
                        setHeadElements(parsedElements);
                    }
                } else {
                    console.log('❌ ページが見つかりません:', { searchSlug, originalSlug: slug });
                    setError(
                        searchSlug === ''
                            ? 'ホームページが見つかりません。管理画面からルートページ（slug: 空またはindex）を作成してください。'
                            : `ページ「${slug}」が見つかりません。`
                    );
                }
            } catch (err) {
                console.error('ページの取得に失敗しました:', err);
                setError('ページの読み込み中にエラーが発生しました。');
            } finally {
                setLoading(false);
            }
        };

        fetchPage();
    }, [slug]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <div className="error-container">{error}</div>;
    }

    // 静的HTMLファイルが存在する場合
    if (isStaticHTML && staticHTMLContent) {
        return (
            <div
                className="static-html-container"
                dangerouslySetInnerHTML={{ __html: staticHTMLContent }}
            />
        );
    }

    // 従来の方法（Firestoreデータ）
    if (!page) {
        return <div className="error-container">ページが見つかりません。</div>;
    }

    return (
        <>
            <Helmet>
                <title>{page.title}</title>
                <meta name="description" content={page.description} />
                {/* AI-friendly meta tags */}
                <meta name="robots" content="index, follow" />
                <meta name="language" content="ja" />
                <meta name="author" content={page.authorEmail || ''} />
                <link rel="canonical" href={window.location.href} />
                {/* Custom head elements from page and global settings */}
                {headElements.map((element, index) => {
                    const Component = element.type;
                    return <Component key={index} {...element.props} />;
                })}
                {page.jsonld && (
                    <script type="application/ld+json">
                        {JSON.stringify(page.jsonld)}
                    </script>
                )}
            </Helmet>
            <div className="public-page-container">
                {globalSettings && globalSettings.customBody && (
                    <div dangerouslySetInnerHTML={{
                        __html: `<!-- Global Settings Custom Body -->\n${globalSettings.customBody}`
                    }} />
                )}
                {page.css && <style>{page.css}</style>}
                <div className="page-content" dangerouslySetInnerHTML={{ __html: page.body }} />
            </div>
        </>
    );
};

export default PublicPage; 